/**
 * Light/dark theme, applied as `data-theme` on <html> (SPEC §9.2).
 *
 * Two states, Light and Dark — no "system" option by product decision. The OS
 * preference seeds the *first* visit; after that the choice is explicit and
 * persisted, so it does not silently follow a later OS change.
 *
 * The value lives in one module-level store rather than in component state, so
 * the header toggle and any chart that needs to repaint read the same source
 * and stay in step. Components subscribe through `useTheme`
 * (`useSyncExternalStore`), which is why there is no context provider to place.
 *
 * `index.css` holds the actual colours. This module only decides *which* is
 * active and stamps the attribute; it never contains a hex value, so the
 * palette has exactly one home.
 */

import { useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark'

/** Persisted here. Namespaced like the reference's `rft.site`. */
const STORAGE_KEY = 'portal-theme'

function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark'
}

/** The OS preference, used only to seed the first visit. */
function systemPreference(): Theme {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** The stored choice, or null. A disabled/blocked localStorage (private mode)
 *  is a legitimate runtime condition, not a masked error — fall back to the OS. */
function storedTheme(): Theme | null {
  try {
    const value = localStorage.getItem(STORAGE_KEY)
    return isTheme(value) ? value : null
  } catch {
    return null
  }
}

let current: Theme = 'light'
const listeners = new Set<() => void>()

function apply(theme: Theme): void {
  // Always set the attribute (both states are explicit once resolved), so the
  // `:not([data-theme])` OS-default rule in index.css only governs the very
  // first paint before this runs.
  document.documentElement.setAttribute('data-theme', theme)
}

/** Resolve and apply the initial theme. Call once, before React renders, so the
 *  attribute is on <html> as early as the bundle executes (the CSP forbids an
 *  inline pre-paint script, so this is the earliest a chosen theme can win). */
export function initTheme(): void {
  current = storedTheme() ?? systemPreference()
  apply(current)
}

export function setTheme(theme: Theme): void {
  if (theme === current) return
  current = theme
  apply(theme)
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // Storage unavailable (private mode): the choice holds for this session and
    // simply is not remembered. Not an error to surface.
  }
  listeners.forEach((listener) => {
    listener()
  })
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): Theme {
  return current
}

/** The active theme and a setter. Subscribing also makes a component repaint on
 *  a toggle — which is how the ECharts adapters, that read CSS custom properties
 *  at render time, pick up the new palette. */
export function useTheme(): { theme: Theme; setTheme: (theme: Theme) => void } {
  const theme = useSyncExternalStore<Theme>(subscribe, getSnapshot, () => 'light')
  return { theme, setTheme }
}
